// Command dispatcher shared by the menu bar, toolbar, buttons and single-key shortcuts.
import type { Case, Tactic } from '@divorceos/schema';
import { changedSettings, compute, programDefaults, resolveFc4055, restoreDefaults, saveProgramDefaults, TACTICS, TAX_YEAR_OPTIONS, money, type Fc4055Setting } from './model';
import { ROWS } from './rows';
import { useDis, type LeftTab, type RightTab } from './store';
import { snapshotFigures } from './scenarios';
import { DEFAULT_PREVIEW_ITEM, previewTabFor } from './preview/catalogue';

const st = () => useDis.getState();
const activeDoc = () => { const s = st(); return s.docs.find((d) => d.key === s.active) ?? null; };

/** Next FC 4055 setting for the G key: flip the resolved version; land on Automatic when that matches. */
function toggledFc(c: Case): Fc4055Setting {
  const now = resolveFc4055(c);
  const want = now === 'SB343' ? 'PRE_SB343' : 'SB343';
  const auto = resolveFc4055({ taxYear: c.taxYear, settings: { ...c.settings, fc4055Version: 'AUTO' } });
  return want === auto ? 'AUTO' : want;
}

export function act(cmd: string): void {
  const s = st();
  const doc = activeDoc();
  const [head, ...rest] = cmd.split(':');
  const arg = rest.join(':');
  s.setUi({ menu: null });
  switch (head) {
    case 'newCase': s.newCase(); s.toast('New case. Enter incomes on Case Data and children on the Children tab. It is saved on the first change.'); return;
    case 'goStart': s.activate('start'); void s.refreshRecent(); return;
    case 'openDlg': void s.refreshRecent(); s.setUi({ dialog: 'open' }); return;
    case 'open': void s.openCase(arg); return;
    case 'about': s.setUi({ dialog: 'about' }); return;
    default: break;
  }
  if (!doc) {
    if (head === 'settings') s.toast('Open or create a case to change its settings.');
    if (head === 'preview') s.toast('Open or create a case to preview its reports.');
    if (head === 'questionnaire' || head === 'qExport' || head === 'qImport') s.toast('Open or create the case first; the questionnaire is prepared from, and imported into, an open case.');
    if (head === 'import') s.toast('Open or create a case first; imported figures fill the open case.');
    return;
  }
  const c = doc.data;
  switch (head) {
    case 'save': void s.save().then(() => st().toast('Saved. disOS also saves every change automatically.')); return;
    case 'saveDefault': saveProgramDefaults(c.settings); s.toast('This case’s settings are now your defaults for new cases.'); return;
    case 'closeCase': s.closeDoc(doc.key); return;
    case 'questionnaire': case 'qExport': case 'qImport': s.setUi({ dialog: 'questionnaire' }); return;
    case 'import': s.setUi({ dialog: 'import' }); return;
    case 'settings': s.setUi({ dialog: 'settings', settingsTab: (arg || s.ui.settingsTab) as typeof s.ui.settingsTab }); return;
    case 'forms': s.setUi({ dialog: 'forms' }); return;
    case 'compare': s.setUi({ dialog: 'compare' }); return;
    case 'tab': s.setUi({ left: arg as LeftTab }); return;
    case 'r': s.setUi({ left: s.ui.left === 'reports' ? 'data' : s.ui.left, right: rest[0] as RightTab, helpTopic: rest[1] ?? null }); return;
    case 'rep': s.setUi({ left: 'reports', report: arg }); return;
    case 'bench': s.setUi({ left: 'reports', report: 'bench' }); return;
    case 'keys': s.setUi({ left: s.ui.left === 'reports' ? 'data' : s.ui.left, right: 'help', helpTopic: null }); return;
    case 'whatsnew': s.setUi({ left: s.ui.left === 'reports' ? 'data' : s.ui.left, right: 'help', helpTopic: 'sb343' }); return;
    case 'expandAll': case 'collapseAll':
      s.setPrefs((p) => { for (const r of ROWS) if (r.subs) p.expanded[r.id] = head === 'expandAll'; });
      s.setUi({ left: 'data' });
      return;
    case 'toggleExpand': act(ROWS.some((r) => r.subs && doc.prefs.expanded[r.id]) ? 'collapseAll' : 'expandAll'); return;
    case 'toggleZero': s.setUi({ hideZeroGrid: !s.ui.hideZeroGrid }); return;
    case 'restoreAuto':
      s.update((x) => { x.parties[0].exemptions.auto = true; x.parties[1].exemptions.auto = true; });
      s.toast('Dependents are counted automatically again.');
      return;
    case 'restoreDefaults': s.update((x) => restoreDefaults(x, programDefaults())); s.toast('Default settings restored for this case.'); return;
    case 'toggleLia': s.update((x) => { x.settings.displayLowIncomeRange = !x.settings.displayLowIncomeRange; }); return;
    case 'per': s.update((x) => { x.mode = arg === 'Y' ? 'ANNUAL' : 'MONTHLY'; }); return;
    case 'togglePer': s.update((x) => { x.mode = x.mode === 'ANNUAL' ? 'MONTHLY' : 'ANNUAL'; }); return;
    case 'nextYear': {
      const i = TAX_YEAR_OPTIONS.indexOf(c.taxYear);
      const y = TAX_YEAR_OPTIONS[(i + 1) % TAX_YEAR_OPTIONS.length] ?? c.taxYear;
      s.update((x) => { x.taxYear = y; });
      s.toast(`Tax year ${y}${y >= 2025 && (c.settings.fc4055Version ?? 'PRE_SB343') === 'AUTO' ? ': FC 4055 as amended applies automatically.' : '.'}`);
      return;
    }
    case 'toggle343': {
      const next = toggledFc(c);
      s.update((x) => { x.settings.fc4055Version = next; });
      const after = { ...c, settings: { ...c.settings, fc4055Version: next } };
      let total = '';
      try { total = ` Guideline total ${money(c, compute(after).guideline.total.amount)}.`; } catch { /* engine error shown on the Results tab */ }
      s.toast(`FC 4055 ${resolveFc4055(after) === 'SB343' ? 'from' : 'before'} 1 Sep 2024.${total}`);
      return;
    }
    case 'nextTactic': {
      const ids = TACTICS.map((t) => t.id);
      const next = (ids[(ids.indexOf(c.proposed.tactic) + 1) % ids.length] ?? '9') as Tactic;
      s.update((x) => { x.proposed.tactic = next; });
      s.toast(`Tactic ${next}`);
      return;
    }
    case 'toggleAuto': s.setUi({ autoRecalc: !s.ui.autoRecalc }); if (!s.ui.autoRecalc) s.recalc(); return;
    case 'toggleTips': s.setUi({ tooltips: !s.ui.tooltips }); return;
    case 'toggleRdp':
      s.update((x) => { x.settings.domesticPartnership = !x.settings.domesticPartnership; });
      s.setPrefs((p) => { p.relation = c.settings.domesticPartnership ? 'married' : 'rdp'; });
      s.toast(c.settings.domesticPartnership ? 'Married: labels follow the case.' : 'Registered domestic partners: labels are Partner 1 and Partner 2.');
      return;
    case 'recalc': s.recalc(); return;
    case 'addScenario': {
      let figures;
      try { figures = snapshotFigures(c, compute(c)); } catch (e) { s.toast(`Cannot add: ${e instanceof Error ? e.message : String(e)}`); return; }
      const n = doc.prefs.scenarios.length + 1;
      s.setPrefs((p) => { p.scenarios.push({ title: `Snapshot ${n} (${money(c, figures.total)})`, savedAt: new Date().toISOString(), figures }); });
      s.setUi({ left: 'reports', report: 'compare' });
      s.toast(`Added “Snapshot ${n}” to the snapshot comparison (saved with the case).`);
      return;
    }
    case 'preview': {
      // Remembered per case in memory (ui.previewTab); an argument (Reports tab id) opens that report.
      const tab = arg ? previewTabFor(arg) : s.ui.previewTab[doc.key] ?? DEFAULT_PREVIEW_ITEM;
      s.setUi({ preview: true, dialog: null, previewTab: { ...s.ui.previewTab, [doc.key]: tab } });
      return;
    }
    case 'closePreview': s.setUi({ preview: false }); return;
    case 'print': {
      // Court packet: when settings differ from default, attach the Settings report so the
      // printout discloses exactly what changed (C2).
      const base = arg || s.ui.report;
      const isCourt = base === 'bench' || base === 'formal' || base === 'summary';
      const attachSettings = isCourt && changedSettings(c).length > 0;
      // C15: auto-append the Smith/Ostler bonus attachment to court prints when the case asks for it.
      const attachBonus = isCourt && doc.prefs.report.appendBonus;
      const set = [base, ...(attachBonus ? ['smith'] : []), ...(attachSettings ? ['settings'] : [])];
      s.setUi({ print: set });
      return;
    }
    case 'printChecked': {
      const ids = Object.keys(s.ui.checked).filter((k) => s.ui.checked[k]);
      if (ids.length === 0) { s.toast('Tick at least one report to print.'); return; }
      // C15: if a court report is checked and the case asks to append the bonus, add it once (not already checked).
      const hasCourt = ids.some((id) => id === 'bench' || id === 'formal' || id === 'summary');
      if (hasCourt && doc.prefs.report.appendBonus && !ids.includes('smith')) ids.push('smith');
      s.setUi({ print: ids });
      return;
    }
    default: return;
  }
}

/** Single-key map (focus not in a text field). */
export const KEYMAP: Record<string, string> = {
  '1': 'tab:data', '2': 'tab:children', '3': 'tab:support', '4': 'tab:taxes', '5': 'tab:parties', '6': 'tab:qbid', '7': 'tab:taxdata', '8': 'tab:reports',
  r: 'r:results', f: 'r:cash', n: 'r:notes', h: 'r:help', '?': 'r:help', s: 'settings', t: 'nextTactic', y: 'nextYear', m: 'togglePer',
  g: 'toggle343', l: 'toggleLia', x: 'toggleExpand', z: 'toggleZero', p: 'bench', c: 'addScenario', v: 'rep:variation', b: 'rep:smith', e: 'rep:releases', w: 'forms', o: 'preview',
};
